import React from 'react';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';
import { colors } from '@/lib/theme/colors';
import { Pill, PillVariant } from '@/components/ui/pill';
import { HUB_PAGE_CHROME } from '@/components/shared/HubTable/tokens';

interface PageHeaderProps {
  /**
   * Main page title
   */
  title: string;

  /**
   * Optional subtitle or description
   */
  description?: string;

  /**
   * Optional icon to display before title
   */
  icon?: LucideIcon;

  /**
   * Optional badge text (e.g., count, filter type)
   * Displayed as a pill next to the title
   * Can be string, number, or ReactNode for custom badges
   */
  badge?: string | number | React.ReactNode;

  /**
   * Badge variant
   * @default 'neutral'
   */
  badgeVariant?: PillVariant;

  /**
   * Optional actions (buttons, filters, etc.) rendered on the right
   */
  actions?: React.ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Size variant for the title
   * @default 'default'
   */
  size?: 'sm' | 'default' | 'lg';

  /**
   * Whether to include border and background (standard page header styling)
   * @default true
   */
   withBorder?: boolean;

  /**
   * Extra classes on the outer `<header>` when `withBorder` is true (e.g. sticky positioning).
   */
  headerClassName?: string;

  /**
   * When true, omits the `md:pr-80` right-padding that reserves space for the floating nav.
   * Use on pages where the floating nav is hidden (e.g. dashboard).
   * @default false
   */
  noReserveRight?: boolean;

  /**
   * `hub` (default) — table-aligned px-4, ~68px band balancing floating top-right nav.
   * `legacy` — larger px-responsive / 24px titles for non-app-shell pages.
   * @default 'hub'
   */
  variant?: 'hub' | 'legacy';
}

/**
 * PageHeader - Unified page header component
 *
 * Provides consistent page header layout with optional icon, badge, title, description, and action slots.
 * Responsive design: stacks on mobile, side-by-side on desktop.
 *
 * @example
 * // Basic usage - title only
 * <PageHeader title="Mailboxes" />
 *
 * @example
 * // With description
 * <PageHeader
 *   title="Workflow Analytics"
 *   description="Track performance across all your campaigns"
 * />
 *
 * @example
 * // With icon and badge
 * <PageHeader
 *   title="Tasks"
 *   description="Review and engage with signals"
 *   icon={Zap}
 *   badge={42}
 * />
 *
 * @example
 * // With icon and text badge
 * <PageHeader
 *   title="Signals Hub"
 *   description="Multi-channel visitor intelligence platform"
 *   icon={Zap}
 *   badge="All Signals"
 *   badgeVariant="neutral"
 * />
 *
 * @example
 * // With actions
 * <PageHeader
 *   title="Mailboxes"
 *   description="Manage your connected email accounts"
 *   actions={
 *     <Button>
 *       <Plus className="w-4 h-4 mr-2" />
 *       Add Mailbox
 *     </Button>
 *   }
 * />
 *
 * @example
 * // Without border (for custom layouts)
 * <PageHeader
 *   title="Dashboard"
 *   withBorder={false}
 *   actions={
 *     <div className="flex items-center gap-3">
 *       <FilterSearch value={q} onChange={setQ} />
 *       <Button>Export</Button>
 *     </div>
 *   }
 * />
 */
// Typography specs (defined outside component to prevent hydration issues)
const titleStylesMap = {
  sm: { fontSize: '20px', fontWeight: 600, lineHeight: '28px' } as const,
  default: { fontSize: '24px', fontWeight: 600, lineHeight: '32px' } as const,
  lg: { fontSize: '30px', fontWeight: 600, lineHeight: '38px' } as const,
};

// Default title style for headers with icons
const defaultHeaderTitleStyle = { fontSize: '18px', fontWeight: 600, lineHeight: '28px' } as const;

// Icon gradient background style (defined outside to prevent hydration issues)
// Using gray/dark colors instead of purple/indigo
const iconGradientStyle = {
  background: `linear-gradient(135deg, ${colors.status.neutral.base}, ${colors.status.neutral.dark})`,
} as const;

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  icon: Icon,
  badge,
  badgeVariant = 'neutral',
  actions,
  className,
  size = 'default',
  withBorder = true,
  headerClassName,
  variant = 'hub',
  noReserveRight = false,
}) => {
  const isHub = variant !== 'legacy';

  const titleStyle = isHub
    ? undefined
    : size === 'sm'
      ? titleStylesMap.sm
      : size === 'lg'
        ? titleStylesMap.lg
        : Icon
          ? defaultHeaderTitleStyle
          : titleStylesMap.default;

  const content = (
    <div className={cn(
      'flex flex-col sm:flex-row sm:justify-between w-full',
      isHub ? 'gap-3 sm:items-center' : 'gap-4 sm:items-start',
      className,
    )}>
      <div className="flex items-center gap-3 min-w-0">
        {/* Icon */}
        {Icon && (
          <div
            className={cn(isHub ? HUB_PAGE_CHROME.iconBox : 'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm')}
            style={iconGradientStyle}
          >
            <Icon className={isHub ? HUB_PAGE_CHROME.icon : 'w-5 h-5 text-white'} />
          </div>
        )}

        {/* Title, badge, and description */}
        <div className={cn('min-w-0', isHub ? 'space-y-0.5' : 'space-y-1')}>
          <div className={cn('flex items-center gap-2 min-w-0', isHub ? 'min-h-[24px]' : 'min-h-[28px]')}>
            <h1
              // truncate: names are user-supplied (audience/campaign names run long) —
              // ellipsize rather than push the badge out or overflow the header band.
              className={cn('truncate', isHub ? HUB_PAGE_CHROME.title : 'text-slate-900')}
              style={titleStyle}
            >
              {title}
            </h1>
            {badge !== undefined && (
              typeof badge === 'number' ? (
                // Thousands separators: hub counts reach five and six figures
                // (27965 vs 27,965), and the tables below already group their
                // own totals — an ungrouped header read as a different number.
                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full leading-none tabular-nums">
                  {badge.toLocaleString()}
                </span>
              ) : typeof badge === 'string' ? (
                <Pill
                  value={badge}
                  variant={badgeVariant}
                  showIcon={false}
                  size="sm"
                  className="flex-shrink-0"
                />
              ) : (
                <div className="flex items-center gap-2">
                  {badge}
                </div>
              )
            )}
          </div>
          {description && (
            <p className={cn('truncate', isHub ? HUB_PAGE_CHROME.description : 'text-sm text-slate-500')}>
              {description}
            </p>
          )}
        </div>
      </div>

      {/* Actions slot */}
      {actions && (
        <div className="flex-shrink-0">
          {actions}
        </div>
      )}
    </div>
  );

  if (withBorder) {
    return (
      <header
        className={cn(
          'border-b border-slate-200 bg-white flex items-center',
          isHub
            ? cn(
              HUB_PAGE_CHROME.paddingX,
              HUB_PAGE_CHROME.headerPaddingY,
              HUB_PAGE_CHROME.headerMinHeight,
              !noReserveRight && HUB_PAGE_CHROME.headerReserveRight,
            )
            : 'px-responsive py-4',
          headerClassName,
        )}
      >
        {content}
      </header>
    );
  }

  return content;
};
